import { MUIFlatTableColumn } from "../components/universal_mui_table/MUIFlatTable";
import { shortFormatDateString } from "./date_format";
import dayjs from "./dayjs";

import { Box, TableCell, Tooltip } from "@mui/material";
import * as arrow from "apache-arrow";
import CompactTableTextField from "../components/transformed_custom_view/viewer/CompactTableTextField";
import React from "react";
import { convertValueToType } from "./parquet";
import { formatCurrency, isCurrencyField } from "./currency_fields";

// ============================================================================
// BASIC UTILITY FUNCTIONS
// ============================================================================

// Parse input value helper function
export const parseInputValue = (inputValue: string): number | null => {
  if (!inputValue || inputValue === "" || inputValue === "-") {
    return null;
  }
  const cleanValue = inputValue.replace(/,/g, "");
  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? null : parsed;
};

// Get planner override value utility function
export const getPlannerOverride = (
  plannerOverrideInputs: Record<string, Record<string, string>>,
  rowKey: string,
  monthName: string
): number | null => {
  const inputValue = plannerOverrideInputs[rowKey]?.[monthName] ?? "";
  return parseInputValue(inputValue);
};

// Export getRowFromArrowTable for use in custom view components
export function getRowFromArrowTable(
  arrowTable: import("apache-arrow").Table,
  columns: import("apache-arrow").Field<any>[],
  rowIdx: number
): Record<string, any> {
  const row: Record<string, any> = {};
  for (let colIdx = 0; colIdx < columns.length; colIdx++) {
    const colName = columns[colIdx].name;
    // Look up the vector by name, not by positional index. `columns` is
    // typically a filtered subset of the arrow schema (e.g. with `status`
    // and `*_count` removed for display), so positional indices into the
    // filtered list don't align with the arrow table's child positions.
    // Using getChild(name) keeps the lookup correct regardless of filter.
    const vector = arrowTable.getChild(colName);
    const rawValue = vector ? vector.get(rowIdx) : null;

    if (rawValue != null && columns[colIdx].type) {
      row[colName] = convertValueToType(rawValue, columns[colIdx].type);
    } else {
      row[colName] = rawValue;
    }
  }
  return row;
}

export const getHeatMapColor = (value: number, theme: any) => {
  if (value === 0) return "transparent";
  if (value < 0) {
    return theme?.palette?.atomic?.red1 || "#ffebee";
  } else {
    return theme?.palette?.atomic?.green1 || "e8f5e9";
  }
};

// ============================================================================
// CELL FORMATTING FUNCTIONS
// ============================================================================

// Extract basic value formatting logic for reuse
export function formatCellValue(
  value: any,
  columnType?: arrow.DataType,
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>,
  col?: string,
  row?: any
): any {
  // Use column formatter if provided
  if (columnFormatters && col && columnFormatters[col]) {
    return columnFormatters[col](value, row);
  }

  // Handle date/timestamp types
  if (
    columnType &&
    (columnType.typeId === arrow.Type.Timestamp ||
      columnType.typeId === arrow.Type.Date)
  ) {
    if (typeof value === "number") {
      return dayjs.utc(value).format("MM/DD/YY");
    } else if (value instanceof Date) {
      return dayjs.utc(value).format("MM/DD/YY");
    } else {
      return value;
    }
  }

  // Handle numeric types
  if (typeof value === "bigint" || typeof value === "number") {
    const numeric = typeof value === "bigint" ? Number(value) : value;
    if (isCurrencyField(col)) {
      return formatCurrency(numeric, col);
    }
    return Math.round(numeric).toLocaleString();
  }

  // Handle dates
  if (value instanceof Date) {
    return dayjs.utc(value).format("MM/DD/YY");
  }

  // Handle strings and fallback
  if (typeof value === "string") {
    return value;
  }

  return value ?? "";
}

export function getFormattedCell({
  value,
  col,
  columnFormatters,
  row,
  theme,
  isDelta = false,
  align,
  columnType,
  headerType,
  shadeHistory = false,
}: {
  value: any;
  col: string;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  row?: any;
  theme?: any;
  isDelta?: boolean;
  align?: string;
  columnType?: arrow.DataType;
  headerType?: arrow.DataType;
  shadeHistory?: boolean;
}) {
  // Use the extracted formatting logic
  const formattedValue = formatCellValue(
    value,
    columnType,
    columnFormatters,
    col,
    row
  );

  // Determine background color
  let backgroundColor;
  if (
    isDelta &&
    theme &&
    (typeof value === "number" || typeof value === "bigint")
  ) {
    backgroundColor = getHeatMapColor(Number(value), theme);
  } else if (
    shadeHistory &&
    isDateColumn({ headerType } as any) &&
    isHistoricalDate(col)
  ) {
    backgroundColor = theme?.palette?.action?.hover;
  }

  const justifyContent =
    align === "center"
      ? "center"
      : align === "right"
        ? "flex-end"
        : undefined;

  return (
    <Box
      sx={{
        p: 1,
        height: "40px",
        display: "flex",
        alignItems: "center",
        ...(justifyContent && { justifyContent }),
        backgroundColor,
      }}
    >
      {formattedValue}
    </Box>
  );
}

export const getFormattedHeader = (
  columnHeaderMap: Record<string, string>,
  type: arrow.DataType,
  sortEntry:
    | { key: string; direction: "asc" | "desc"; index: number }
    | undefined,
  col: any
) => {
  return (
    <Box sx={{ userSelect: "none" }}>
      {formatHeaderLabel(columnHeaderMap, type, col)}
      {sortEntry?.direction === "asc"
        ? " ▲"
        : sortEntry?.direction === "desc"
          ? " ▼"
          : ""}

      {sortEntry && sortEntry?.index !== undefined && (
        <Box
          sx={{
            fontSize: "12px",
            marginLeft: 1,
            display: "inline-block",
          }}
        >
          ({sortEntry.index + 1})
        </Box>
      )}
    </Box>
  );
};

export const formatColumnCaption = (string: string): string => {
  return string.toLowerCase().replaceAll("_", " ");
};

export const formatHeaderLabel = (
  columnHeaderMap: Record<string, string>,
  type: arrow.DataType,
  header: any
) => {
  if (columnHeaderMap[header]) {
    return columnHeaderMap[header];
  }
  switch (type.typeId) {
    case arrow.Type.Utf8:
    case arrow.Type.LargeUtf8:
      return formatColumnCaption(header);
    case arrow.Type.Date:
    case arrow.Type.Timestamp: {
      let dateObj = header;
      if (typeof header === "string" || typeof header === "number") {
        dateObj = dayjs(Number(header));
      }
      return shortFormatDateString(dateObj);
    }
    case arrow.Type.Int:
    case arrow.Type.Int8:
    case arrow.Type.Int16:
    case arrow.Type.Int32:
    case arrow.Type.Int64:
    case arrow.Type.Uint8:
    case arrow.Type.Uint16:
    case arrow.Type.Uint32:
    case arrow.Type.Uint64:
    case arrow.Type.Float:
    case arrow.Type.Float16:
    case arrow.Type.Float32:
    case arrow.Type.Float64:
      return header.toLocaleString();
    case arrow.Type.Bool:
      return header === true ? "True" : header === false ? "False" : header;
    default:
      return header;
  }
};

// ============================================================================
// SORTING FUNCTIONS
// ============================================================================

// Replace getSortedRows to sort using Arrow vectors and row indices
export const getSortedRowIndices = (
  arrowTable: arrow.Table,
  columns: arrow.Field<any>[],
  sortConfig: Array<{ key: string; direction: "asc" | "desc" }>,
  isComparison = false
): number[] => {
  if (!arrowTable || !columns || !sortConfig.length) {
    return Array.from({ length: arrowTable.numRows }, (_, i) => i);
  }

  const colNames = columns.map((col) => col.name);
  // Precompute column indices for sort keys
  const sortColIndices = sortConfig.map(({ key }) => colNames.indexOf(key));

  // Special run order for comparison tables
  const runOrder = ["1st", "2nd", "delta (1st - 2nd)"];
  const tableIdColIndex = colNames.indexOf("table_id");
  const tableIdVector =
    tableIdColIndex >= 0 ? arrowTable.getChildAt(tableIdColIndex) : null;

  return Array.from({ length: arrowTable.numRows }, (_, i) => i).sort(
    (a, b) => {
      // For comparison tables, first sort by run order (table_id)
      if (isComparison && tableIdVector) {
        const aTableId = tableIdVector.get(a);
        const bTableId = tableIdVector.get(b);
        const aRunIdx = runOrder.indexOf(String(aTableId));
        const bRunIdx = runOrder.indexOf(String(bTableId));
        if (aRunIdx !== bRunIdx) {
          return aRunIdx - bRunIdx;
        }
      }

      // Then apply user-specified sorting (skip table_id for comparisons since it's handled above)
      for (let i = 0; i < sortConfig.length; i++) {
        const { key, direction } = sortConfig[i];

        // Skip table_id sorting for comparison tables since we handled it above
        if (isComparison && key === "table_id") continue;

        const colIdx = sortColIndices[i];
        if (colIdx === -1) continue;

        const vector = arrowTable.getChildAt(colIdx);
        const aVal = vector ? vector.get(a) : undefined;
        const bVal = vector ? vector.get(b) : undefined;

        if (aVal == null && bVal == null) continue;
        if (aVal == null) return direction === "asc" ? -1 : 1;
        if (bVal == null) return direction === "asc" ? 1 : -1;
        if (aVal < bVal) return direction === "asc" ? -1 : 1;
        if (aVal > bVal) return direction === "asc" ? 1 : -1;
      }
      return 0;
    }
  );
};

// ============================================================================
// WIDTH CALCULATION UTILITIES
// ============================================================================

// Calculate width for a column based on its longest unique value
export const calculateColumnWidth = (
  columnNames: string | string[],
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >
): number | undefined => {
  const names = Array.isArray(columnNames) ? columnNames : [columnNames];

  if (!columnSchema) return undefined;

  let totalChars = 0;
  let hasValidData = false;

  for (const columnName of names) {
    if (columnSchema[columnName]?.unique_values) {
      const uniqueValues = columnSchema[columnName].unique_values || [];
      const validValues = uniqueValues.filter(
        (value) => value != null && value !== undefined
      );
      if (validValues.length > 0) {
        const longestValue = validValues.reduce(
          (longest, current) =>
            current.length > longest.length ? current : longest,
          validValues[0]
        );
        totalChars += longestValue.length;
        hasValidData = true;
      }
    }
  }

  if (hasValidData) {
    const valueChars = totalChars;
    const columnTitleChars =
      (Array.isArray(columnNames)
        ? columnNames.reduce((acc, col) => {
            return acc + col.length;
          }, 0)
        : columnNames.length) + 10;
    // column names are given extra px padding to account for sorting arrow and index
    return valueChars > columnTitleChars
      ? valueChars * 6
      : columnTitleChars * 6;
  }

  return undefined;
};

// ============================================================================
// TABLE BUILDING FUNCTIONS
// ============================================================================

// Utility: Build MUI columns from Arrow fields and config
export function buildMUIColumns({
  columns,
  sortConfig = [],
  onSort,
  extraColumns = [],
  stickyFirst = false,
  columnTypeOverrides = {},
  columnSchema,
  columnHeaderMap,
}: {
  columns: arrow.Field<any>[];
  sortConfig?: Array<{ key: string; direction: "asc" | "desc" }>;
  onSort?: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  extraColumns?: any[];
  stickyFirst?: boolean;
  columnTypeOverrides?: Record<string, arrow.DataType>;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  columnHeaderMap?: Record<string, string>;
}) {
  return [
    ...columns.map((colObj, idx) => {
      return {
        key: colObj.name,
        label:
          columnHeaderMap?.[colObj.name] || formatColumnCaption(colObj.name), // Use header map or format the column name
        sortable: !!onSort,
        cellType: columnTypeOverrides[colObj.name] || colObj.type,
        headerType: colObj.type, // Use actual Arrow column type instead of always Utf8
        sticky: false,
        onSort: onSort
          ? (e: React.MouseEvent<HTMLTableCellElement>) =>
              onSort(colObj.name, e)
          : undefined,
        sortDirection: sortConfig.find((s) => s.key === colObj.name)?.direction,
      };
    }),
    ...extraColumns,
  ];
}

// Utility: Build a getRow function for virtualization from an Arrow Table
export function makeGetRowFromArrowTable(
  arrowTable: arrow.Table,
  columns: arrow.Field<any>[]
) {
  return (rowIndex: number) =>
    getRowFromArrowTable(arrowTable, columns, rowIndex);
}

// Utility: Build a getRow function for virtualization from a visible row index mapping
export function makeGetRowFromArrowTableWithIndices(
  arrowTable: arrow.Table,
  columns: arrow.Field<any>[],
  visibleRows: number[]
) {
  return (rowIndex: number) =>
    getRowFromArrowTable(arrowTable, columns, visibleRows[rowIndex]);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Helper function to check if a column is a date column
export const isDateColumn = (column: MUIFlatTableColumn): boolean => {
  return (
    column.headerType?.typeId === arrow.Type.Timestamp ||
    column.headerType?.typeId === arrow.Type.Date
  );
};

// Helper function to check if a column is a date column based on schema dtype
export const isDateColumnBySchema = (
  columnName: string,
  columnSchema: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >
): boolean => {
  const dtype = columnSchema[columnName]?.dtype;
  if (!dtype) return false;
  return (
    dtype === "Datetime(time_unit='us', time_zone='UTC')" ||
    dtype === "Date" ||
    dtype.startsWith("Datetime") ||
    dtype.startsWith("Timestamp")
  );
};

// Helper function to check if a date value is in the past or today
export const isHistoricalDate = (
  dateValue: string | number | Date
): boolean => {
  if (!dateValue) return false;

  let date: dayjs.Dayjs;

  if (typeof dateValue === "number") {
    // Value is timestamp in milliseconds
    date = dayjs.utc(dateValue);
  } else if (dateValue instanceof Date) {
    // Value is a Date object
    date = dayjs.utc(dateValue);
  } else if (
    typeof dateValue === "string" &&
    dateValue.match(/^\d{2}\/\d{2}\/\d{2}$/)
  ) {
    // Parse MM/DD/YY format specifically
    date = dayjs.utc(dateValue, "MM/DD/YY");
  } else if (typeof dateValue === "string" && !isNaN(Number(dateValue))) {
    // If it's a string that is a number, treat as epoch ms
    date = dayjs.utc(Number(dateValue));
  } else {
    // Try to parse as generic date
    date = dayjs.utc(dateValue);
  }

  const now = dayjs.utc();
  return date.isSameOrBefore(now, "day");
};

// Find the first segmentation column index - use data analysis instead of sort config
export const getFirstSegmentationColumnIndex = (
  columns: MUIFlatTableColumn[],
  getRow: (index: number) => Record<string, any>,
  rowCount: number
): number => {
  // Look for the first column that contains non-numeric values (segmentation data)
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    if (col.key === "table_id") continue; // Skip table_id column

    // Check a few rows to see if this column contains non-numeric data
    let hasNonNumericData = false;
    for (let j = 0; j < Math.min(rowCount, 5); j++) {
      const row = getRow(j);
      const value = row[col.key];
      if (typeof value !== "number" && value !== null && value !== undefined) {
        hasNonNumericData = true;
        break;
      }
    }

    if (hasNonNumericData) {
      return i;
    }
  }

  // Fallback: if no segmentation columns found, default to first column
  return 0;
};

// Check if a column is a segmentation column (excluding table_id)
export const isSegmentationColumn = (
  colKey: string,
  getRow: (index: number) => Record<string, any>,
  rowCount: number
): boolean => {
  if (colKey === "table_id") return false;

  // Check if this column contains non-numeric values (segmentation data)
  for (let i = 0; i < Math.min(rowCount, 5); i++) {
    const row = getRow(i);
    const value = row[colKey];
    if (typeof value !== "number" && value !== null && value !== undefined) {
      return true;
    }
  }

  return false;
};

export const getFormattedTotalCell = ({
  col,
  index,
  firstSegmentationIndex,
  columnTotals,
  getRow,
  rowCount,
  theme,
  tableId,
  isComparison = false,
  isSticky = false,
  stickyLeftOffset = 0,
}: {
  col: {
    key: string;
    align?: "left" | "center" | "right";
    currencyKey?: string;
  };
  index: number;
  firstSegmentationIndex: number;
  columnTotals?:
    | Record<string, number>
    | Array<{ tableId: string; totals: Record<string, number> }>;
  getRow: (index: number) => Record<string, any>;
  rowCount: number;
  theme: any;
  tableId?: string;
  isComparison?: boolean;
  isSticky?: boolean;
  stickyLeftOffset?: number;
}) => {
  let total: number | undefined;

  if (isComparison && Array.isArray(columnTotals) && tableId) {
    // For comparison tables, find the totals for this specific tableId
    const tableData = columnTotals.find((t) => t.tableId === tableId);
    total = tableData?.totals[col.key];
  } else if (!Array.isArray(columnTotals)) {
    // For regular tables, use the totals object directly
    total = columnTotals?.[col.key];
  }

  const currencyLookupKey = col.currencyKey ?? col.key;
  const formattedTotal =
    typeof total === "number"
      ? isCurrencyField(currencyLookupKey)
        ? formatCurrency(total, currencyLookupKey)
        : total.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })
      : "";

  let displayValue: string;
  if (isComparison && col.key === "table_id") {
    displayValue = tableId || "";
  } else if (index === firstSegmentationIndex) {
    displayValue = "Total";
  } else if (isSegmentationColumn(col.key, getRow, rowCount)) {
    displayValue = "";
  } else {
    displayValue = formattedTotal;
  }

  const backgroundColor =
    isComparison && tableId === "delta (1st - 2nd)" && typeof total === "number"
      ? getHeatMapColor(total, theme)
      : theme.palette.grey[50];

  return (
    <TableCell
      key={isComparison ? `total-${tableId}-${col.key}` : `total-${col.key}`}
      align={col.align || "left"}
      sx={{
        backgroundColor,
        color: theme.palette.text.primary,
        fontWeight: "bold",
        ...(isSticky && {
          position: "sticky",
          left: stickyLeftOffset,
        }),
      }}
    >
      {displayValue}
    </TableCell>
  );
};

// Reusable function to calculate column totals using Arrow native operations
export function calculateColumnTotals({
  arrowTable,
  columns,
  hasTableId = false,
}: {
  arrowTable: arrow.Table;
  columns: { key: string; cellType?: arrow.DataType }[];
  hasTableId?: boolean;
}):
  | Record<string, number>
  | Array<{ tableId: string; totals: Record<string, number> }> {
  if (hasTableId) {
    // For comparison tables, return array of totals objects
    const tableIdVector = arrowTable.getChild("table_id");
    if (!tableIdVector) {
      console.log("calculateColumnTotals: No table_id vector found");
      return [];
    }

    const tableIdGroups: Record<string, Record<string, number>> = {};

    // Single pass through Arrow vectors
    for (let i = 0; i < arrowTable.numRows; i++) {
      const tableId = String(tableIdVector.get(i) || "unknown");

      if (!tableIdGroups[tableId]) {
        tableIdGroups[tableId] = {};
      }

      columns.forEach((col) => {
        if (col.key !== "table_id") {
          const vector = arrowTable.getChild(col.key);
          if (vector && isNumericColumn(col.cellType || vector.type)) {
            const value = vector.get(i);
            if (
              value !== null &&
              value !== undefined &&
              !isNaN(Number(value))
            ) {
              tableIdGroups[tableId][col.key] =
                (tableIdGroups[tableId][col.key] || 0) + Number(value);
            }
          }
        }
      });
    }

    // Return array of totals objects
    const result = Object.entries(tableIdGroups).map(([tableId, totals]) => ({
      tableId,
      totals,
    }));

    return result;
  } else {
    // For regular tables, return single totals object
    const totals: Record<string, number> = {};

    columns.forEach((col) => {
      const vector = arrowTable.getChild(col.key);

      if (vector && isNumericColumn(col.cellType || vector.type)) {
        let total = 0;
        for (let i = 0; i < vector.length; i++) {
          const value = vector.get(i);
          if (value !== null && value !== undefined && !isNaN(Number(value))) {
            total += Number(value);
          }
        }
        totals[col.key] = total;
      }
    });

    return totals;
  }
}

// Helper function to check if a column type is numeric
function isNumericColumn(type: arrow.DataType): boolean {
  // Use the actual type ID values we're seeing in the debug output
  const numericTypeIds = [
    2, // Int64 (as seen in debug)
    3, // Float64 (as seen in debug)
    1, // Int32 (common)
    4, // Float32 (common)
    // Add other common numeric type IDs
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    13,
    14,
    15,
  ];

  return numericTypeIds.includes(type.typeId);
}

// ============================================================================
// DEMAND WALK SINGLE UTILITIES
// ============================================================================

export const safeNumber = (value: any): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "bigint") return Number(value);
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

export const getSegmentationFields = (
  viewParams: any,
  columns: arrow.Field<any>[] | null
): string[] => {
  if (viewParams?.segments && viewParams.segments.length > 0) {
    return viewParams.segments;
  }
  if (columns) {
    const excludeFields = [
      "period_group",
      "month_name",
      "month_rank",
      "baseline_qty",
      "period_sort",
      "quantity",
      "revenue",
      "category",
      "year",
    ];
    return columns
      .filter((col) => !excludeFields.includes(col.name))
      .map((col) => col.name);
  }
  return [];
};

export const getAggregateField = (viewParams: any): string => {
  return viewParams?.measure || "quantity";
};

interface ProcessedTableRow {
  [key: string]: any;
  category_type?: string;
  year?: string;
  baseline_qty?: number;
  period_sort?: number;
  monthData?: Record<string, any>;
}

interface SegmentationGroup {
  [key: string]: any;
  baseline_qty?: number;
  period_sort?: number;
}

interface RowMetadata {
  type: "data" | "planner-override" | "final-forecast" | "yoy";
  sourceRowIndex?: number;
  segKey: string;
  segmentationData?: Record<string, any>;
}

export const processTableData = (
  arrowTable: arrow.Table,
  columns: arrow.Field<any>[],
  segmentationFields: string[]
): {
  tableData: ProcessedTableRow[];
  monthNames: string[];
  segmentationGroups: SegmentationGroup[];
} => {
  // Get vectors once and create efficient lookup
  const columnVectors = columns.map((col) => arrowTable.getChild(col.name));
  const columnNameMap = new Map(columns.map((col, idx) => [col.name, idx]));

  // Efficient row value access using vector indices
  const getRowValue = (rowIdx: number, columnName: string) => {
    const colIdx = columnNameMap.get(columnName);
    if (colIdx === undefined) return null;
    const vector = columnVectors[colIdx];
    return vector ? vector.get(rowIdx) : null;
  };

  // Use Maps for efficient lookups instead of arrays
  const monthMap = new Map<
    string,
    { month_name: string; month_rank: number }
  >();
  const tableMap = new Map<string, Record<string, any>>();
  const segmentationGroupMap = new Map<string, Record<string, any>>();

  // Single pass through Arrow data to build all lookup structures
  for (let i = 0; i < arrowTable.numRows; i++) {
    const monthName = getRowValue(i, "month_name");
    const monthRank = getRowValue(i, "month_rank");
    const category_type = getRowValue(i, "category_type");
    const year = getRowValue(i, "year");
    const baselineQty = getRowValue(i, "baseline_qty");
    const periodSort = getRowValue(i, "period_sort");

    if (monthName && monthRank !== undefined) {
      monthMap.set(monthName, {
        month_name: monthName,
        month_rank: safeNumber(monthRank),
      });
    }

    const segKey = segmentationFields
      .map((f: string) => getRowValue(i, f))
      .join("|");
    const key = `${segKey}||${category_type}||${year}`;

    if (!segmentationGroupMap.has(segKey)) {
      segmentationGroupMap.set(segKey, {
        ...Object.fromEntries(
          segmentationFields.map((f: string) => [f, getRowValue(i, f)])
        ),
        baseline_qty: safeNumber(baselineQty),
        period_sort: safeNumber(periodSort),
      });
    }

    if (!tableMap.has(key)) {
      tableMap.set(key, {
        ...Object.fromEntries(
          segmentationFields.map((f: string) => [f, getRowValue(i, f)])
        ),
        category_type: category_type,
        year: year,
        baseline_qty: safeNumber(baselineQty),
        period_sort: safeNumber(periodSort),
        monthData: {},
      });
    }

    const currentRow = tableMap.get(key)!;
    if (monthName) {
      // Store the Arrow row index instead of converting to JS object immediately
      currentRow.monthData[monthName] = i; // Store Arrow row index
    }
  }

  // Convert month data to sorted array only when needed
  const monthNames = Array.from(monthMap.values())
    .sort((a, b) => a.month_rank - b.month_rank)
    .map((m) => m.month_name);

  // Convert table data to sorted array only when needed
  const tableData = Array.from(tableMap.values()).sort((a, b) => {
    if (b.baseline_qty !== a.baseline_qty) {
      return b.baseline_qty - a.baseline_qty;
    }
    return a.period_sort - b.period_sort;
  }) as ProcessedTableRow[];

  // Convert segmentation groups to sorted array only when needed
  const segmentationGroups = Array.from(segmentationGroupMap.values()).sort(
    (a, b) => {
      if (b.baseline_qty !== a.baseline_qty) {
        return b.baseline_qty - a.baseline_qty;
      }
      return a.period_sort - b.period_sort;
    }
  ) as SegmentationGroup[];

  return { tableData, monthNames, segmentationGroups };
};

export const createCombinedTableMetadata = (
  tableData: ProcessedTableRow[],
  segmentationGroups: SegmentationGroup[],
  segmentationFields: string[]
): {
  sortedMetadata: RowMetadata[];
  combinedArrowTable: arrow.Table;
} => {
  const rowMetadata: RowMetadata[] = [];

  // Build metadata efficiently without unnecessary conversions
  tableData.forEach((tableRow, i) => {
    const segKey = segmentationFields.map((f: string) => tableRow[f]).join("|");
    rowMetadata.push({
      type: "data",
      sourceRowIndex: i,
      segKey,
    });
  });

  segmentationGroups.forEach((segGroup) => {
    const segKey = segmentationFields.map((f: string) => segGroup[f]).join("|");

    rowMetadata.push(
      {
        type: "planner-override" as const,
        segKey,
        segmentationData: segGroup,
      },
      {
        type: "final-forecast" as const,
        segKey,
        segmentationData: segGroup,
      },
      {
        type: "yoy" as const,
        segKey,
        segmentationData: segGroup,
      }
    );
  });

  // Sort metadata efficiently
  const sortedMetadata = rowMetadata.sort((a, b) => {
    const aBaselineQty =
      a.segmentationData?.baseline_qty ||
      (a.sourceRowIndex !== undefined
        ? safeNumber(tableData[a.sourceRowIndex].baseline_qty)
        : 0);
    const bBaselineQty =
      b.segmentationData?.baseline_qty ||
      (b.sourceRowIndex !== undefined
        ? safeNumber(tableData[b.sourceRowIndex].baseline_qty)
        : 0);

    if (bBaselineQty !== aBaselineQty) {
      return bBaselineQty - aBaselineQty;
    }

    if (a.segKey !== b.segKey) {
      return a.segKey.localeCompare(b.segKey);
    }

    const getCategoryOrder = (meta: RowMetadata) => {
      if (meta.type === "planner-override") return 100;
      if (meta.type === "final-forecast") return 101;
      if (meta.type === "yoy") return 102;

      if (meta.sourceRowIndex !== undefined) {
        const sourceRow = tableData[meta.sourceRowIndex];
        switch (sourceRow.category_type) {
          case "History":
            return 0;
          case "Baseline Forecast":
            return 1;
          case "Planner Adjustment":
            return 2;
          case "Final Forecast":
            return 3;
          case "YoY%":
            return 4;
          default:
            return 5;
        }
      }
      return 999;
    };

    const aCategoryOrder = getCategoryOrder(a);
    const bCategoryOrder = getCategoryOrder(b);

    if (aCategoryOrder !== bCategoryOrder) {
      return aCategoryOrder - bCategoryOrder;
    }

    const aYear =
      a.segmentationData?.year ||
      (a.sourceRowIndex !== undefined
        ? String(tableData[a.sourceRowIndex].year || "")
        : "");
    const bYear =
      b.segmentationData?.year ||
      (b.sourceRowIndex !== undefined
        ? String(tableData[b.sourceRowIndex].year || "")
        : "");

    if (aYear !== bYear) {
      return aYear.localeCompare(bYear);
    }

    return 0;
  });

  // Build Arrow table data efficiently - only convert to arrays at the last moment
  const metadataArrowData = {
    row_type: sortedMetadata.map((m) => m.type),
    seg_key: sortedMetadata.map((m) => m.segKey),
    source_row_index: sortedMetadata.map((m) => m.sourceRowIndex ?? -1),
  };

  // Create Arrow table from the final data
  const combinedArrowTable = arrow.tableFromArrays(metadataArrowData);

  return { sortedMetadata, combinedArrowTable };
};

export const createGetRowFunction = (
  sortedMetadata: RowMetadata[],
  tableData: ProcessedTableRow[],
  segmentationFields: string[],
  monthNames?: string[],
  aggregateField?: string,
  demandWalkConfig?: {
    plannerOverrideInputs: Record<string, Record<string, string>>;
    finalForecastInputs: Record<string, Record<string, string>>;
    yoyPercentageInputs: Record<string, Record<string, string>>;
  },
  originalArrowTable?: arrow.Table,
  originalColumns?: arrow.Field<any>[]
) => {
  // Helper function to get planner override from the input data
  const getPlannerOverrideForRow = (
    rowKey: string,
    monthName: string
  ): number | null => {
    if (!demandWalkConfig?.plannerOverrideInputs) return null;
    // Use month index as the key for simplicity
    if (!monthNames) return null;
    const monthIndex = monthNames.indexOf(monthName);
    return getPlannerOverride(
      demandWalkConfig.plannerOverrideInputs,
      rowKey,
      monthIndex.toString()
    );
  };

  return (rowIndex: number): Record<string, any> => {
    if (rowIndex >= sortedMetadata.length) return {};

    const metadata = sortedMetadata[rowIndex];
    const result: Record<string, any> = {
      row_type: metadata.type,
      seg_key: metadata.segKey,
    };

    if (metadata.type === "data" && metadata.sourceRowIndex !== undefined) {
      const sourceRow = tableData[metadata.sourceRowIndex];

      segmentationFields.forEach((field: string) => {
        result[field] = sourceRow[field] || "";
      });

      result.category_type = sourceRow.category_type || "";
      result.year = sourceRow.year || "";
      result._sourceData = sourceRow;
    } else {
      segmentationFields.forEach((field: string) => {
        result[field] = metadata.segmentationData?.[field] || "";
      });

      result.category_type =
        metadata.type === "planner-override"
          ? "Planner Override"
          : metadata.type === "final-forecast"
            ? "Final Forecast"
            : metadata.type === "yoy"
              ? "YoY%"
              : "";

      result.year = "";
      result._segmentationData = metadata.segmentationData;
    }

    // Add calculated month values for export compatibility
    if (monthNames && aggregateField && demandWalkConfig) {
      const rowKey = result.seg_key;

      monthNames.forEach((monthName) => {
        let value: any = "";

        if (result.row_type === "planner-override") {
          const override = getPlannerOverrideForRow(rowKey, monthName);
          value = override === null ? "" : override;
        } else if (result.row_type === "final-forecast") {
          const baselineRow = tableData.find(
            (r) =>
              r.category_type === "Baseline Forecast" &&
              segmentationFields.every((f: string) => r[f] === result[f])
          );

          // Get baseline value from Arrow table if available, otherwise from processed data
          let baselineValue = 0;
          if (originalArrowTable && originalColumns && baselineRow?.monthData) {
            const arrowRowIndex = baselineRow.monthData[monthName];
            if (typeof arrowRowIndex === "number") {
              const aggregateVector =
                originalArrowTable.getChild(aggregateField);
              if (aggregateVector) {
                baselineValue = Number(aggregateVector.get(arrowRowIndex)) || 0;
              }
            }
          } else {
            baselineValue = Number(
              baselineRow?.monthData?.[monthName]?.[aggregateField] || 0
            );
          }

          const override = Number(
            getPlannerOverrideForRow(rowKey, monthName) || 0
          );
          value = baselineValue + override;
        } else if (result.row_type === "yoy") {
          const baselineRow = tableData.find(
            (r) =>
              r.category_type === "Baseline Forecast" &&
              segmentationFields.every((f: string) => r[f] === result[f])
          );

          // Get baseline value from Arrow table if available, otherwise from processed data
          let baselineValue = 0;
          if (originalArrowTable && originalColumns && baselineRow?.monthData) {
            const arrowRowIndex = baselineRow.monthData[monthName];
            if (typeof arrowRowIndex === "number") {
              const aggregateVector =
                originalArrowTable.getChild(aggregateField);
              if (aggregateVector) {
                baselineValue = Number(aggregateVector.get(arrowRowIndex)) || 0;
              }
            }
          } else {
            baselineValue = Number(
              baselineRow?.monthData?.[monthName]?.[aggregateField] || 0
            );
          }

          const override = Number(
            getPlannerOverrideForRow(rowKey, monthName) || 0
          );

          // Use finalForecastInputs if available, otherwise calculate from baseline + override
          let finalForecast = baselineValue + override;
          if (demandWalkConfig?.finalForecastInputs && monthNames) {
            const monthIndex = monthNames.indexOf(monthName);
            if (monthIndex >= 0) {
              const finalForecastInput =
                demandWalkConfig.finalForecastInputs[rowKey]?.[
                  monthIndex.toString()
                ];
              if (
                finalForecastInput !== undefined &&
                finalForecastInput !== ""
              ) {
                const parsedFinalForecast = parseInputValue(finalForecastInput);
                if (parsedFinalForecast !== null) {
                  finalForecast = parsedFinalForecast;
                }
              }
            }
          }

          // Get highest history year for this segment
          const historyRows = tableData.filter(
            (row) =>
              row.category_type === "History" &&
              segmentationFields.every((f: string) => {
                const segValue = row[f];
                const segGroupValue = tableData.find(
                  (g) =>
                    segmentationFields.map((sf: string) => g[sf]).join("|") ===
                    rowKey
                )?.[f];
                return segValue === segGroupValue;
              })
          );

          const years = historyRows
            .map((row) => {
              const yearStr = String(row.year || "");
              const yearMatch = yearStr.match(/(\d{4})/);
              return yearMatch ? parseInt(yearMatch[1]) : 0;
            })
            .filter((year) => year > 0);

          const highestHistoryYear = years.length > 0 ? Math.max(...years) : 0;

          const historyRow = tableData.find(
            (r) =>
              r.category_type === "History" &&
              segmentationFields.every((f: string) => r[f] === result[f]) &&
              String(r.year).includes(String(highestHistoryYear))
          );

          // Get history value from Arrow table if available, otherwise from processed data
          let historyValue = 0;
          if (originalArrowTable && originalColumns && historyRow?.monthData) {
            const arrowRowIndex = historyRow.monthData[monthName];
            if (typeof arrowRowIndex === "number") {
              const aggregateVector =
                originalArrowTable.getChild(aggregateField);
              if (aggregateVector) {
                historyValue = Number(aggregateVector.get(arrowRowIndex)) || 0;
              }
            }
          } else {
            historyValue = Number(
              historyRow?.monthData?.[monthName]?.[aggregateField] || 0
            );
          }

          if (historyValue > 0) {
            const yoyPercent =
              ((finalForecast - historyValue) / historyValue) * 100;
            value = `${Math.round(yoyPercent)}%`;
          } else {
            value = "N/A";
          }
        } else if (result.row_type === "data" && result._sourceData) {
          const sourceData = result._sourceData;

          if (sourceData.category_type === "Planner Adjustment") {
            // Planner Adjustment functionality removed - always return 0
            value = 0;
          } else {
            // Get month data from Arrow table if available, otherwise from processed data
            const matchingRow = tableData.find(
              (td) =>
                td.category_type === sourceData.category_type &&
                td.year === sourceData.year &&
                segmentationFields.every((f: string) => td[f] === sourceData[f])
            );

            if (
              originalArrowTable &&
              originalColumns &&
              matchingRow?.monthData
            ) {
              const arrowRowIndex = matchingRow.monthData[monthName];
              if (typeof arrowRowIndex === "number") {
                const aggregateVector =
                  originalArrowTable.getChild(aggregateField);
                if (aggregateVector) {
                  const rawValue = aggregateVector.get(arrowRowIndex);
                  value =
                    rawValue !== null && rawValue !== undefined
                      ? typeof rawValue === "number"
                        ? rawValue
                        : parseFloat(String(rawValue)) || 0
                      : 0;
                }
              }
            } else {
              const monthData = matchingRow?.monthData?.[monthName];
              value =
                monthData &&
                monthData !== undefined &&
                monthData[aggregateField] !== null &&
                monthData[aggregateField] !== undefined
                  ? typeof monthData[aggregateField] === "number"
                    ? monthData[aggregateField]
                    : parseFloat(monthData[aggregateField]) || 0
                  : 0;
            }
          }
        }

        result[monthName] = value;
      });
    }

    return result;
  };
};

export const createDemandWalkColumns = ({
  segmentationFields,
  monthNames,
  tableData,
  aggregateField,
  demandWalkConfig,
  getRow,
  columnSchema,
  originalArrowTable,
  originalColumns,
}: {
  segmentationFields: string[];
  monthNames: string[];
  tableData: ProcessedTableRow[];
  aggregateField: string;
  demandWalkConfig: {
    plannerOverrideInputs: Record<string, Record<string, string>>;
    finalForecastInputs: Record<string, Record<string, string>>;
    yoyPercentageInputs: Record<string, Record<string, string>>;
    setPlannerOverrideInputs: React.Dispatch<
      React.SetStateAction<Record<string, Record<string, string>>>
    >;
    setFinalForecastInputs: React.Dispatch<
      React.SetStateAction<Record<string, Record<string, string>>>
    >;
    setYoYPercentageInputs: React.Dispatch<
      React.SetStateAction<Record<string, Record<string, string>>>
    >;
    editMode?: boolean;
    showPersistedOverrideMarkers?: boolean;
  };
  getRow: (index: number) => Record<string, any>;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  originalArrowTable?: arrow.Table;
  originalColumns?: arrow.Field<any>[];
}): MUIFlatTableColumn[] => {
  // Extract from demandWalkConfig
  const {
    plannerOverrideInputs,
    finalForecastInputs,
    yoyPercentageInputs,
    setPlannerOverrideInputs,
    setFinalForecastInputs,
    setYoYPercentageInputs,
    editMode = false,
    showPersistedOverrideMarkers = false,
  } = demandWalkConfig;

  // ── Persisted-override metadata reader (Demand Plan only) ──
  // Reads the override columns at the Arrow row index in monthData[monthName];
  // null when the cell isn't overridden or the feature is off → no marker/hover.
  const getOverrideMetaForCell = (
    monthData: any
  ): {
    baseline: number;
    override: number;
    delta: number;
    editedBy: string | null;
    editedAt: string | null;
    kind: "direct" | "coarser" | "finer";
  } | null => {
    if (!showPersistedOverrideMarkers) return null;
    if (!originalArrowTable || typeof monthData !== "number") return null;

    const isOverriddenVec = originalArrowTable.getChild("is_overridden");
    if (!isOverriddenVec) return null;
    const isOverridden = Boolean(isOverriddenVec.get(monthData));
    if (!isOverridden) return null;

    const num = (col: string): number => {
      const v = originalArrowTable.getChild(col)?.get(monthData);
      return v === null || v === undefined ? 0 : Number(v) || 0;
    };
    const str = (col: string): string | null => {
      const v = originalArrowTable.getChild(col)?.get(monthData);
      return v === null || v === undefined ? null : String(v);
    };

    const baseline = num("baseline");
    const override = num("quantity_override");
    const editedAtRaw = originalArrowTable.getChild("edited_at")?.get(monthData);
    let editedAt: string | null = null;
    if (editedAtRaw !== null && editedAtRaw !== undefined) {
      const d = dayjs.utc(editedAtRaw);
      if (d.isValid()) editedAt = shortFormatDateString(d);
    }

    // override_grain = the level name(s) the override was applied at. Vs the displayed
    // segmentation: direct (same fields), coarser (override ⊂ displayed → fanned down /
    // inherited), or finer (override has a field not shown → rolled up into this cell).
    const grain = str("override_grain");
    const displayedFields = [...segmentationFields].map((s) => s.trim());
    const overrideFields =
      grain != null
        ? grain
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s !== "")
        : [];
    const allOverrideShown =
      overrideFields.length > 0 &&
      overrideFields.every((f) => displayedFields.includes(f));
    let kind: "direct" | "coarser" | "finer";
    if (allOverrideShown && overrideFields.length === displayedFields.length) {
      kind = "direct";
    } else if (allOverrideShown) {
      kind = "coarser"; // override fields ⊂ displayed → fanned down (inherited)
    } else {
      kind = "finer"; // override has a field not displayed → rolled up
    }

    return {
      baseline,
      override,
      delta: override - baseline,
      editedBy: str("edited_by"),
      editedAt,
      kind,
    };
  };

  // Helper functions for planner logic (using exported utilities)
  const getPlannerOverrideForRow = (
    rowKey: string,
    monthName: string
  ): number | null => {
    // Use month index as the key for simplicity
    if (!monthNames) return null;
    const monthIndex = monthNames.indexOf(monthName);
    return getPlannerOverride(
      plannerOverrideInputs,
      rowKey,
      monthIndex.toString()
    );
  };

  // Helper function to get value from Arrow table or processed data
  const getValueFromMonthData = (monthData: any, monthName: string): number => {
    if (
      originalArrowTable &&
      originalColumns &&
      typeof monthData === "number"
    ) {
      // monthData is an Arrow row index
      const aggregateVector = originalArrowTable.getChild(aggregateField);
      if (aggregateVector) {
        const value = aggregateVector.get(monthData);
        return value !== null && value !== undefined ? Number(value) || 0 : 0;
      }
    } else if (monthData && typeof monthData === "object") {
      // monthData is a JS object (fallback)
      const value = monthData[aggregateField];
      return value !== null && value !== undefined ? Number(value) || 0 : 0;
    }
    return 0;
  };

  // ── Demand Plan (persistent override) display helpers ───────────────────
  // All gated on showPersistedOverrideMarkers → the shared Demand Walk (Beta) grid
  // is byte-for-byte unchanged.

  // Read an arbitrary numeric arrow column at a monthData row index. Falls back
  // to the aggregate field when the column is absent (non-Demand-Plan grid).
  const getColumnFromMonthData = (
    monthData: any,
    columnName: string
  ): number => {
    if (originalArrowTable && originalColumns && typeof monthData === "number") {
      const vec = originalArrowTable.getChild(columnName);
      if (vec) {
        const v = vec.get(monthData);
        return v !== null && v !== undefined ? Number(v) || 0 : 0;
      }
    } else if (monthData && typeof monthData === "object") {
      const v = monthData[columnName];
      if (v !== null && v !== undefined) return Number(v) || 0;
    }
    // Column absent → behave exactly like getValueFromMonthData.
    return getValueFromMonthData(monthData, "");
  };

  // The clean pre-override baseline: the engine's `baseline` column on the Demand
  // Plan grid, else just the displayed value.
  const getBaselineQtyForCell = (
    baselineRow: any,
    monthName: string
  ): number => {
    const monthData = baselineRow?.monthData?.[monthName];
    if (showPersistedOverrideMarkers) {
      return getColumnFromMonthData(monthData, "baseline");
    }
    return getValueFromMonthData(monthData, monthName);
  };

  // The override already BAKED INTO the run (final − clean baseline). Zero when
  // not a Demand Plan grid or the cell wasn't overridden.
  const getPersistedOverrideForCell = (
    baselineRow: any,
    monthName: string
  ): number => {
    if (!showPersistedOverrideMarkers) return 0;
    const monthData = baselineRow?.monthData?.[monthName];
    const finalVal = getValueFromMonthData(monthData, monthName);
    const baseVal = getColumnFromMonthData(monthData, "baseline");
    // Round: final − baseline is a difference of daily sums and can carry float
    // noise; overrides are unit counts so a whole number is the honest display.
    return Math.round(finalVal - baseVal);
  };

  // The EFFECTIVE override for display: this session's edit wins; otherwise the
  // override persisted from a prior save (so a saved override shows on load).
  const getEffectiveOverrideForRow = (
    rowKey: string,
    monthName: string,
    baselineRow: any
  ): number => {
    // Demand Plan only: a blanked final cell is a revert-in-progress → no override.
    // Gated so the Beta grid keeps its prior semantics (clearing yields the override).
    if (showPersistedOverrideMarkers) {
      const monthIndex = monthNames ? monthNames.indexOf(monthName) : -1;
      const ffRaw =
        monthIndex >= 0
          ? finalForecastInputs[rowKey]?.[monthIndex.toString()]
          : undefined;
      if (ffRaw === "" || ffRaw === "-") return 0;
    }
    const session = getPlannerOverrideForRow(rowKey, monthName);
    if (session !== null && session !== undefined) return session;
    return getPersistedOverrideForCell(baselineRow, monthName);
  };

  // True when a cell has an override worth a Revert affordance (persisted or
  // this session), and isn't already mid-revert. Demand Plan grid only.
  const cellHasRevertableOverride = (
    rowKey: string,
    monthName: string,
    baselineRow: any
  ): boolean => {
    if (!showPersistedOverrideMarkers) return false;
    const monthIndex = monthNames ? monthNames.indexOf(monthName) : -1;
    const ffRaw =
      monthIndex >= 0
        ? finalForecastInputs[rowKey]?.[monthIndex.toString()]
        : undefined;
    if (ffRaw === "" || ffRaw === "-") return false; // already reverting
    return (
      Math.abs(getEffectiveOverrideForRow(rowKey, monthName, baselineRow)) > 0.5
    );
  };

  // Blank the final cell = the REVERT signal: convertDemandPlanInputsToParquet turns
  // it into a clearedKey, and the provider's merge deletes the saved override on Save.
  const revertCell = (rowKey: string, monthName: string): void => {
    const monthIndex = monthNames ? monthNames.indexOf(monthName) : -1;
    if (monthIndex < 0) return;
    const monthKey = monthIndex.toString();
    setFinalForecastInputs((prev) => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], [monthKey]: "" },
    }));
    // Clear any lingering session override / YoY entry for the same cell so the
    // derived rows read a clean revert.
    setPlannerOverrideInputs((prev) => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], [monthKey]: "" },
    }));
    setYoYPercentageInputs((prev) => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], [monthKey]: "" },
    }));
  };

  // Helper function for YoY color logic (used in demand walk columns)
  const getYoYColor = (percent: number) => {
    if (percent > 0) return "#e8f5e9"; // green
    if (percent < 0) return "#ffebee"; // red
    return "#fff3e0"; // orange
  };

  // Create segmentation columns
  const segmentationColumns: MUIFlatTableColumn[] = segmentationFields.map(
    (field) => ({
      key: field,
      label: field,
      align: "left" as const,
      renderCell: (row, rowIndex) => {
        const value = row[field] || "";

        // Bold if it's the first row OR if ANY segmentation field changed (new segment group)
        let shouldBolden = false;
        if (rowIndex === 0) {
          shouldBolden = true;
        } else {
          const previousRow = getRow(rowIndex - 1);
          // Check if any segmentation field changed from the previous row
          // This indicates a new segment group, so all fields should be bolded
          const anyFieldChanged = segmentationFields.some(
            (segField) =>
              (previousRow[segField] || "") !== (row[segField] || "")
          );
          shouldBolden = anyFieldChanged;
        }

        return (
          <Box
            sx={{
              p: 1,
              height: "40px",
              display: "flex",
              alignItems: "center",
              backgroundColor:
                row.category_type === "History" ? "#f5f5f5" : "transparent",
              fontWeight: shouldBolden ? "bold" : "normal",
            }}
          >
            {shouldBolden ? String(value) : ""}
          </Box>
        );
      },
      width: calculateColumnWidth(field, columnSchema),
      sticky: true,
    })
  );

  // Create category column (always exists, renamed to avoid key collision)
  const categoryColumn: MUIFlatTableColumn = {
    key: "category_type",
    label: "category type",
    align: "left" as const,
    width: 150,
    sticky: true,
    renderCell: (row) => (
      <Box
        sx={{
          p: 1,
          height: "40px",
          display: "flex",
          alignItems: "center",
          backgroundColor:
            row.category_type === "History" ? "#f5f5f5" : "transparent",
          fontWeight: "bold",
        }}
      >
        {row.category_type || ""}
      </Box>
    ),
  };

  // Create year column
  const yearColumn: MUIFlatTableColumn = {
    key: "year",
    label: "year",
    align: "left" as const,
    width: 80,
    sticky: true,
    renderCell: (row) => (
      <Box
        sx={{
          p: 1,
          height: "40px",
          display: "flex",
          alignItems: "center",
          backgroundColor:
            row.category_type === "History" ? "#f5f5f5" : "transparent",
        }}
      >
        {row.year || ""}
      </Box>
    ),
  };

  // Helper function removed - replaced with direct state updates

  // Create month columns
  const monthColumns: MUIFlatTableColumn[] = monthNames.map(
    (monthName, monthIndex) => {
      // Convert month name to MM/DD/YY format for display
      const monthToDisplayFormat = (
        monthName: string,
        monthIndex: number
      ): string => {
        const monthNames = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const monthIndexInYear = monthNames.findIndex(
          (m) => m.toLowerCase() === monthName.toLowerCase()
        );
        if (monthIndexInYear === -1) return monthName; // fallback to original

        const month = monthIndexInYear + 1;

        // Calculate the correct year for this month in the sequence
        // The monthNames array represents the next 12 months from some starting point
        // We need to calculate the year based on the month value and current date
        // Use UTC dates to ensure consistency
        const now = new Date();
        const currentMonth = now.getUTCMonth() + 1; // 1-12 (January = 1, December = 12)
        const currentYear = now.getUTCFullYear();

        // Calculate the year for this month
        // If this month comes before the current month in the calendar year, it's next year
        let year: number;
        if (month < currentMonth) {
          year = currentYear + 1;
        } else {
          year = currentYear;
        }

        const yearShort = year.toString().slice(-2);

        return `${month.toString().padStart(2, "0")}/01/${yearShort}`;
      };

      return {
        key: monthName,
        label: monthToDisplayFormat(monthName, monthIndex),
        align: "left" as const,
        renderCell: (row, rowIndex) => {
          const rowKey = row.seg_key;
          const backgroundColor =
            row.category_type === "History" ? "#f5f5f5" : "transparent";

          if (row.row_type === "planner-override") {
            const baselineRow = tableData.find(
              (r) =>
                r.category_type === "Baseline Forecast" &&
                segmentationFields.every((f: string) => r[f] === row[f])
            );
            // Effective = this session's edit if any, else the override baked
            // into the run (so a previously-saved override is visible on load).
            const override = getEffectiveOverrideForRow(
              rowKey,
              monthName,
              baselineRow
            );
            const displayValue =
              override === null || override === 0 ? "" : String(override);

            if (editMode) {
              // Edit mode: Show editable input field
              // Use month index as key for simplicity
              const monthKey = monthIndex.toString();
              const inputValue =
                plannerOverrideInputs[rowKey]?.[monthKey] ?? displayValue;

              // In edit mode, show empty string if value is "0" so user can edit it
              const editValue = inputValue === "0" ? "" : inputValue;

              return (
                <Box
                  sx={{
                    p: 1,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor,
                  }}
                >
                  <CompactTableTextField
                    value={editValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      setPlannerOverrideInputs((prev) => ({
                        ...prev,
                        [rowKey]: {
                          ...prev[rowKey],
                          [monthKey]: value,
                        },
                      }));

                      // Use the CLEAN baseline (outer baselineRow) so final = baseline
                      // + override stays consistent with the displayed baseline row.
                      const baselineValue = getBaselineQtyForCell(
                        baselineRow,
                        monthName
                      );

                      const overrideValue = parseInputValue(value) || 0;
                      let newFinalForecast = baselineValue + overrideValue;

                      // Edge case: If planner override is 0, final forecast should be baseline forecast
                      if (overrideValue === 0) {
                        newFinalForecast = baselineValue;
                      }

                      setFinalForecastInputs((prev) => ({
                        ...prev,
                        [rowKey]: {
                          ...prev[rowKey],
                          [monthKey]: newFinalForecast.toLocaleString(),
                        },
                      }));
                    }}
                  />
                </Box>
              );
            } else {
              // View mode: Show read-only display
              // Show user input if present, otherwise show calculated value
              const monthKey = monthIndex.toString();
              const inputValue =
                plannerOverrideInputs[rowKey]?.[monthKey] ?? displayValue;

              // Show '-' if value is empty, null, or "0"
              const displayText =
                inputValue && inputValue !== "0" ? inputValue : "-";

              return (
                <Box
                  sx={{
                    p: 1,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor,
                  }}
                >
                  {displayText}
                </Box>
              );
            }
          } else if (row.row_type === "final-forecast") {
            const baselineRow = tableData.find(
              (r) =>
                r.category_type === "Baseline Forecast" &&
                segmentationFields.every((f: string) => r[f] === row[f])
            );
            const baselineValue = getBaselineQtyForCell(baselineRow, monthName);
            const override = safeNumber(
              getEffectiveOverrideForRow(rowKey, monthName, baselineRow) || 0
            );
            const finalForecast = baselineValue + override;

            if (editMode) {
              // Edit mode: Show editable input field
              // Use month index as key for simplicity
              const monthKey = monthIndex.toString();
              const ffRaw = finalForecastInputs[rowKey]?.[monthKey];
              // Blanked final cell = revert-in-progress (Demand Plan only): the STORED
              // value stays blank (the Save signal to DELETE the override) but we DISPLAY
              // the clean baseline. Gated so Beta keeps its prior blank-cell behavior.
              const isReverting =
                showPersistedOverrideMarkers && (ffRaw === "" || ffRaw === "-");
              const finalForecastInputValue = isReverting
                ? formatCellValue(baselineValue)
                : ffRaw ?? formatCellValue(finalForecast);
              const showRevert = cellHasRevertableOverride(
                rowKey,
                monthName,
                baselineRow
              );

              return (
                <Box
                  sx={{
                    p: 1,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor,
                    fontWeight: "bold",
                  }}
                >
                  <CompactTableTextField
                    value={finalForecastInputValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      setFinalForecastInputs((prev) => ({
                        ...prev,
                        [rowKey]: {
                          ...prev[rowKey],
                          [monthKey]: value,
                        },
                      }));

                      // Calculate the new override based on final forecast input
                      const targetFinalForecast = parseInputValue(value) || 0;
                      let newOverride = targetFinalForecast - baselineValue;

                      // Edge case: If final forecast is 0, planner override should be negative of baseline forecast
                      if (targetFinalForecast === 0) {
                        newOverride = -baselineValue;
                      }

                      // Update planner override input to reflect the calculated override
                      setPlannerOverrideInputs((prev) => ({
                        ...prev,
                        [rowKey]: {
                          ...prev[rowKey],
                          [monthKey]:
                            newOverride === 0
                              ? ""
                              : newOverride.toLocaleString(),
                        },
                      }));
                    }}
                    sx={{
                      fontWeight: "bold",
                    }}
                  />
                  {showRevert && (
                    <Tooltip
                      arrow
                      title={`Revert to baseline (${formatCellValue(
                        baselineValue
                      )}) — removes the saved override on next run`}
                    >
                      <Box
                        component="span"
                        role="button"
                        aria-label="Revert override to baseline"
                        onClick={() => revertCell(rowKey, monthName)}
                        sx={{
                          cursor: "pointer",
                          color: "#1976d2",
                          fontSize: "1rem",
                          lineHeight: 1,
                          userSelect: "none",
                          flexShrink: 0,
                        }}
                      >
                        ↺
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              );
            } else {
              // View mode: Show read-only display
              // Show user input if present, otherwise show calculated value
              const monthKey = monthIndex.toString();
              const ffRaw = finalForecastInputs[rowKey]?.[monthKey];
              // Blanked final cell = pending revert (Demand Plan only) → show baseline +
              // suppress the marker. Gated so Beta keeps its prior blank-cell behavior.
              const isReverting =
                showPersistedOverrideMarkers && (ffRaw === "" || ffRaw === "-");
              const finalForecastInputValue = isReverting
                ? formatCellValue(baselineValue)
                : ffRaw ?? formatCellValue(finalForecast);

              // Persisted-override marker + hover (Demand Plan only). null when
              // the cell is baseline / reverted (no marker, no hover).
              const overrideMeta = isReverting
                ? null
                : getOverrideMetaForCell(baselineRow?.monthData?.[monthName]);

              const cellBox = (
                <Box
                  sx={{
                    p: 1,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: overrideMeta
                      ? "rgba(25, 118, 210, 0.08)" // subtle tint on overridden cells
                      : backgroundColor,
                    fontWeight: "bold",
                    // Only positioned when it holds the override dot: a blanket
                    // `position: relative` makes cells paint over the sticky (no z-index)
                    // frozen columns and bleed on the wide weekly grid.
                    position: overrideMeta ? "relative" : "static",
                  }}
                >
                  {finalForecastInputValue}
                  {overrideMeta && (
                    // Corner dot: FILLED = direct edit at this grain; OUTLINE = indirect
                    // (a coarser override fanned down, or a finer one rolled up).
                    <Box
                      component="span"
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor:
                          overrideMeta.kind === "direct"
                            ? "#1976d2"
                            : "transparent",
                        border: "1px solid #1976d2",
                      }}
                    />
                  )}
                </Box>
              );

              if (!overrideMeta) return cellBox;

              const deltaSign = overrideMeta.delta >= 0 ? "+" : "";
              return (
                <Tooltip
                  arrow
                  title={
                    <Box sx={{ fontSize: "0.75rem", lineHeight: 1.5 }}>
                      <div>
                        Baseline: {formatCellValue(overrideMeta.baseline)}
                      </div>
                      <div>
                        Override: {formatCellValue(overrideMeta.override)} (
                        {deltaSign}
                        {formatCellValue(overrideMeta.delta)})
                      </div>
                      {overrideMeta.kind === "coarser" && (
                        <div>Inherited from a coarser grain</div>
                      )}
                      {overrideMeta.kind === "finer" && (
                        <div>Includes a finer-grain override</div>
                      )}
                      {overrideMeta.editedBy && (
                        <div>Set by: {overrideMeta.editedBy}</div>
                      )}
                      {overrideMeta.editedAt && (
                        <div>Set on: {overrideMeta.editedAt}</div>
                      )}
                    </Box>
                  }
                >
                  {cellBox}
                </Tooltip>
              );
            }
          } else if (row.row_type === "yoy") {
            const baselineRow = tableData.find(
              (r) =>
                r.category_type === "Baseline Forecast" &&
                segmentationFields.every((f: string) => r[f] === row[f])
            );
            const baselineValue = getBaselineQtyForCell(baselineRow, monthName);
            const override = safeNumber(
              getEffectiveOverrideForRow(rowKey, monthName, baselineRow) || 0
            );

            // Use finalForecastInputs if available, otherwise calculate from baseline + override
            let finalForecast = baselineValue + override;
            const monthKey = monthIndex.toString();
            const finalForecastInput = finalForecastInputs[rowKey]?.[monthKey];
            if (finalForecastInput !== undefined && finalForecastInput !== "") {
              const parsedFinalForecast = parseInputValue(finalForecastInput);
              if (parsedFinalForecast !== null) {
                finalForecast = parsedFinalForecast;
              }
            }

            const highestHistoryYear = getHighestHistoryYear(
              rowKey,
              tableData,
              segmentationFields
            );
            const historyRow = tableData.find(
              (r) =>
                r.category_type === "History" &&
                segmentationFields.every((f: string) => r[f] === row[f]) &&
                String(r.year).includes(String(highestHistoryYear))
            );
            const historyValue = getValueFromMonthData(
              historyRow?.monthData?.[monthName],
              monthName
            );

            let yoyPercent = 0;
            if (historyValue > 0) {
              yoyPercent =
                ((finalForecast - historyValue) / historyValue) * 100;
            }

            // Check if we have a YoY percentage input override
            const yoyPercentageInput = yoyPercentageInputs[rowKey]?.[monthKey];
            if (yoyPercentageInput !== undefined && yoyPercentageInput !== "") {
              const parsedYoYPercent = parseInputValue(yoyPercentageInput);
              if (parsedYoYPercent !== null) {
                yoyPercent = parsedYoYPercent;
              }
            }

            const yoyBackgroundColor =
              historyValue > 0 ? getYoYColor(yoyPercent) : backgroundColor;

            if (editMode) {
              // In edit mode, show editable input field for YoY percentage
              const inputValue =
                yoyPercentageInputs[rowKey]?.[monthKey] ??
                (historyValue > 0 ? `${Math.round(yoyPercent)}` : "");

              return (
                <Box
                  sx={{
                    p: 1,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: yoyBackgroundColor,
                    fontWeight: "bold",
                  }}
                >
                  <CompactTableTextField
                    value={inputValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      setYoYPercentageInputs((prev) => ({
                        ...prev,
                        [rowKey]: {
                          ...prev[rowKey],
                          [monthKey]: value,
                        },
                      }));

                      // Update final forecast and planner override based on YoY percentage
                      if (historyValue > 0 && value !== "") {
                        const parsedYoYPercent = parseInputValue(value);
                        if (parsedYoYPercent !== null) {
                          // Calculate target final forecast from YoY percentage
                          const newFinalForecast =
                            historyValue * (1 + parsedYoYPercent / 100);
                          const roundedFinalForecast =
                            Math.round(newFinalForecast);

                          // Calculate required planner override from rounded final forecast
                          const newPlannerOverride =
                            roundedFinalForecast - baselineValue;

                          // Update final forecast
                          setFinalForecastInputs((prev) => ({
                            ...prev,
                            [rowKey]: {
                              ...prev[rowKey],
                              [monthKey]: roundedFinalForecast.toString(),
                            },
                          }));

                          // Update planner override
                          setPlannerOverrideInputs((prev) => ({
                            ...prev,
                            [rowKey]: {
                              ...prev[rowKey],
                              [monthKey]:
                                Math.round(newPlannerOverride).toString(),
                            },
                          }));
                        }
                      }
                    }}
                    sx={{ fontWeight: "bold" }}
                    placeholder={historyValue > 0 ? "YoY %" : "N/A"}
                    disabled={historyValue <= 0}
                  />
                </Box>
              );
            } else {
              // In view mode, show display-only value
              return (
                <Box
                  sx={{
                    p: 1,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: yoyBackgroundColor,
                    fontWeight: "bold",
                  }}
                >
                  {historyValue > 0 ? `${Math.round(yoyPercent)}%` : "N/A"}
                </Box>
              );
            }
          } else {
            // Handle data rows
            const sourceData = row._sourceData;
            if (!sourceData)
              return (
                <Box
                  sx={{
                    p: 1,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor,
                  }}
                />
              );

            const monthData = tableData.find(
              (td) =>
                td.category_type === sourceData.category_type &&
                td.year === sourceData.year &&
                segmentationFields.every((f: string) => td[f] === sourceData[f])
            )?.monthData?.[monthName];

            // Demand Plan "Baseline Forecast" row shows the CLEAN pre-override value
            // (`baseline` column); the engine bakes the override into the aggregate.
            // Every other row/grid is unchanged.
            const value =
              showPersistedOverrideMarkers &&
              sourceData.category_type === "Baseline Forecast"
                ? getColumnFromMonthData(monthData, "baseline")
                : getValueFromMonthData(monthData, monthName);

            if (sourceData.category_type === "Planner Adjustment") {
              // Planner Adjustment functionality removed - show read-only display
              return (
                <Box
                  sx={{
                    p: 1,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor,
                    color: "#999",
                  }}
                >
                  N/A
                </Box>
              );
            } else if (sourceData.category_type === "Final Forecast") {
              const baselineRow = tableData.find(
                (r) =>
                  r.category_type === "Baseline Forecast" &&
                  segmentationFields.every(
                    (f: string) => r[f] === sourceData[f]
                  ) &&
                  r.year === sourceData.year
              );
              const baselineValue = getValueFromMonthData(
                baselineRow?.monthData?.[monthName],
                monthName
              );
              // Simplified: Final forecast = baseline + override (no separate adjustment)
              const override = safeNumber(
                getPlannerOverrideForRow(rowKey, monthName) || 0
              );
              const finalForecast = baselineValue + override;

              if (editMode) {
                // In edit mode, show editable input field
                const monthKey = monthIndex.toString();
                const inputValue =
                  finalForecastInputs[rowKey]?.[monthKey] ??
                  formatCellValue(finalForecast);

                return (
                  <Box
                    sx={{
                      p: 1,
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor,
                      fontWeight: "bold",
                    }}
                  >
                    <CompactTableTextField
                      value={inputValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        setFinalForecastInputs((prev) => ({
                          ...prev,
                          [rowKey]: {
                            ...prev[rowKey],
                            [monthKey]: value,
                          },
                        }));
                      }}
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                );
              } else {
                // In view mode, show display-only value
                // Show user edit if present, otherwise show calculated finalForecast
                const monthKey = monthIndex.toString();
                const displayValue = finalForecastInputs[rowKey]?.[monthKey]
                  ? formatCellValue(
                      safeNumber(finalForecastInputs[rowKey][monthKey])
                    )
                  : formatCellValue(finalForecast);

                return (
                  <Box
                    sx={{
                      p: 1,
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor,
                      fontWeight: "bold",
                    }}
                  >
                    {displayValue}
                  </Box>
                );
              }
            } else if (sourceData.category_type === "YoY%") {
              const historyRow = tableData.find(
                (r) =>
                  r.category_type === "History" &&
                  segmentationFields.every(
                    (f: string) => r[f] === sourceData[f]
                  ) &&
                  r.year === sourceData.year
              );
              const historyValue = getValueFromMonthData(
                historyRow?.monthData?.[monthName],
                monthName
              );

              const baselineRow = tableData.find(
                (r) =>
                  r.category_type === "Baseline Forecast" &&
                  segmentationFields.every(
                    (f: string) => r[f] === sourceData[f]
                  ) &&
                  r.year === sourceData.year
              );
              const baselineValue = getValueFromMonthData(
                baselineRow?.monthData?.[monthName],
                monthName
              );
              // Simplified: Final forecast = baseline + override (no separate adjustment)
              const override = safeNumber(
                getPlannerOverrideForRow(rowKey, monthName) || 0
              );

              // Use finalForecastInputs if available, otherwise calculate from baseline + override
              let finalForecast = baselineValue + override;
              const monthKey = monthIndex.toString();
              const finalForecastInput =
                finalForecastInputs[rowKey]?.[monthKey];
              if (
                finalForecastInput !== undefined &&
                finalForecastInput !== ""
              ) {
                const parsedFinalForecast = parseInputValue(finalForecastInput);
                if (parsedFinalForecast !== null) {
                  finalForecast = parsedFinalForecast;
                }
              }

              let yoyPercent = 0;
              if (historyValue > 0) {
                yoyPercent =
                  ((finalForecast - historyValue) / historyValue) * 100;
              }

              const yoyBackgroundColor =
                historyValue > 0 ? getYoYColor(yoyPercent) : backgroundColor;

              return (
                <Box
                  sx={{
                    p: 1,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: yoyBackgroundColor,
                    fontWeight: "bold",
                  }}
                >
                  {historyValue > 0 ? `${Math.round(yoyPercent)}%` : "N/A"}
                </Box>
              );
            } else {
              // Regular display for History and Baseline Forecast
              return (
                <Box
                  sx={{
                    p: 1,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor,
                  }}
                >
                  {value > 0 ? formatCellValue(value) : ""}
                </Box>
              );
            }
          }
        },
      };
    }
  );

  return [...segmentationColumns, categoryColumn, yearColumn, ...monthColumns];
};

// Helper function to get highest history year for a segment
export const getHighestHistoryYear = (
  segKey: string,
  tableData: ProcessedTableRow[],
  segmentationFields: string[]
): number => {
  const historyRows = tableData.filter(
    (row) =>
      row.category_type === "History" &&
      segmentationFields.every((f: string) => {
        const segValue = row[f];
        const segGroupValue = tableData.find(
          (g) =>
            segmentationFields.map((sf: string) => g[sf]).join("|") === segKey
        )?.[f];
        return segValue === segGroupValue;
      })
  );

  if (historyRows.length === 0) return 0;

  const years = historyRows
    .map((row) => {
      const yearStr = String(row.year || "");
      // Extract the first 4-digit year from formats like "2024-25" or "2024"
      const yearMatch = yearStr.match(/(\d{4})/);
      return yearMatch ? parseInt(yearMatch[1]) : 0;
    })
    .filter((year) => year > 0)
    .sort((a, b) => b - a);

  return years.length > 0 ? years[0] : 0;
};

// Helper function to get unique segment combinations directly from Arrow vectors
export function getUniqueSegmentCombosFromVectors(
  segmentVectors: (arrow.Vector | null)[],
  numRows: number
): Map<string, any[]> {
  const comboMap = new Map<string, any[]>();

  // Filter out null vectors
  const validVectors = segmentVectors.filter(
    (v) => v !== null
  ) as arrow.Vector[];

  for (let i = 0; i < numRows; i++) {
    const combo = validVectors.map((vec) => vec.get(i));
    const comboKey = combo.join("|");
    if (!comboMap.has(comboKey)) {
      comboMap.set(comboKey, combo);
    }
  }
  return comboMap;
}

// Helper function to get unique values from a vector without converting to array
export function getUniqueValuesFromVector(vector: arrow.Vector): Set<any> {
  const uniqueValues = new Set();
  for (let i = 0; i < vector.length; i++) {
    uniqueValues.add(vector.get(i));
  }
  return uniqueValues;
}

// ============================================================================
// DEMAND WALK PARQUET CONVERSION UTILITY
// ============================================================================

export interface DemandWalkParquetRow {
  group_level: string[];
  group_value: string[];
  start: number; // microseconds timestamp for parquetjs
  end: number; // microseconds timestamp for parquetjs
  quantity_override: number;
  override_type: string;
}

/**
 * Converts finalForecastInputs to parquet format for demand walk edits
 * @param finalForecastInputs - The user's final forecast input values (channel -> date -> value)
 * @param segments - Array of segmentation field names (e.g., ["channel"])
 * @param measure - The measure field name (e.g., "quantity")
 * @param addSystemMessage - Function to add system error messages
 * @returns Array of parquet rows ready for conversion
 */
export const convertFinalForecastInputsToParquet = (
  finalForecastInputs: Record<string, Record<string, string>>,
  segments: string[],
  measure: string,
  addSystemMessage: (message: { level: any; message: string }) => void
): DemandWalkParquetRow[] => {
  const rows: DemandWalkParquetRow[] = [];

  try {
    // Validate input parameters
    if (!finalForecastInputs || typeof finalForecastInputs !== "object") {
      addSystemMessage({
        level: "danger",
        message:
          "Invalid finalForecastInputs: must be a valid object with channel and date data.",
      });
      return [];
    }

    if (!segments || !Array.isArray(segments) || segments.length === 0) {
      addSystemMessage({
        level: "danger",
        message:
          "Invalid segments: must be a non-empty array of segmentation field names.",
      });
      return [];
    }

    if (!measure || typeof measure !== "string") {
      addSystemMessage({
        level: "danger",
        message:
          "Invalid measure: must be a valid string representing the measure field.",
      });
      return [];
    }

    // Check if there are any inputs to process
    const channelKeys = Object.keys(finalForecastInputs);
    if (channelKeys.length === 0) {
      addSystemMessage({
        level: "warning",
        message:
          "No changes found. Please make some changes before saving or press discard to exit edit mode.",
      });
      return [];
    }

    // Process each channel/segment combination
    for (const [channelKey, dateInputs] of Object.entries(
      finalForecastInputs
    )) {
      // Validate channel key format
      if (!channelKey || typeof channelKey !== "string") {
        addSystemMessage({
          level: "danger",
          message: `Invalid channel key: ${channelKey}. Must be a valid string.`,
        });
        return [];
      }

      // Parse the channel key to extract individual segment values
      // The key format is "segment1|segment2|..." (e.g., "Amazon|SKU-002")
      const segmentValues = channelKey.split("|");

      // Validate that we have the right number of segment values
      if (segmentValues.length !== segments.length) {
        addSystemMessage({
          level: "danger",
          message: `Segment mismatch: Expected ${segments.length} segments but found ${segmentValues.length} in key "${channelKey}". Cannot proceed with conversion.`,
        });
        return [];
      }

      // Validate that all segment values are valid strings
      for (let i = 0; i < segmentValues.length; i++) {
        if (
          !segmentValues[i] ||
          typeof segmentValues[i] !== "string" ||
          segmentValues[i].trim() === ""
        ) {
          addSystemMessage({
            level: "danger",
            message: `Invalid segment value at position ${i + 1} in key "${channelKey}": "${segmentValues[i]}". All segment values must be non-empty strings.`,
          });
          return [];
        }
      }

      // Validate dateInputs
      if (!dateInputs || typeof dateInputs !== "object") {
        addSystemMessage({
          level: "danger",
          message: `Invalid date inputs for channel "${channelKey}": must be a valid object with date keys and numeric values.`,
        });
        return [];
      }

      const dateKeys = Object.keys(dateInputs);
      if (dateKeys.length === 0) {
        addSystemMessage({
          level: "warning",
          message: `No date data found for channel "${channelKey}". Skipping this channel.`,
        });
        continue;
      }

      // Process each date input
      for (const [dateKey, inputValue] of Object.entries(dateInputs)) {
        // Validate date key format
        if (!dateKey || typeof dateKey !== "string") {
          addSystemMessage({
            level: "danger",
            message: `Invalid date key in channel "${channelKey}": ${dateKey}. Must be a valid string.`,
          });
          return [];
        }

        // Validate input value
        if (inputValue === null || inputValue === undefined) {
          addSystemMessage({
            level: "danger",
            message: `Missing input value for date ${dateKey} in channel "${channelKey}". Cannot proceed with conversion.`,
          });
          return [];
        }

        // Parse the numeric value
        const parsedValue = parseInputValue(String(inputValue));
        if (parsedValue === null) {
          addSystemMessage({
            level: "danger",
            message: `Invalid numeric value for date ${dateKey} in channel "${channelKey}": "${inputValue}". Value must be a valid number.`,
          });
          return [];
        }

        // Parse the date key (format: month index as string "0", "1", "2", etc.)
        // Convert month index to actual date
        const monthIndex = parseInt(dateKey, 10);
        if (isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
          addSystemMessage({
            level: "danger",
            message: `Invalid month index for key "${dateKey}" in channel "${channelKey}". Expected a number between 0-11.`,
          });
          return [];
        }

        // Convert month index to actual date
        // The month indices correspond to the actual months in the table
        // The table shows months in chronological order: Sep 2025, Oct 2025, Nov 2025, Dec 2025, Jan 2026, Feb 2026, Mar 2026...
        const currentDate = dayjs().utc();
        const currentYear = currentDate.year();
        const currentMonth = currentDate.month(); // 0-based (0 = January)

        // Calculate the actual month and year based on the table's chronological order
        // The table starts from the current month and shows the next 12 months
        let year = currentYear;
        let actualMonthIndex = monthIndex;

        // Add the month index to the current month to get the actual month
        const totalMonths = currentMonth + monthIndex;

        // Calculate the year and month
        year = currentYear + Math.floor(totalMonths / 12);
        actualMonthIndex = totalMonths % 12;

        // Create start and end dates for the month using UTC to avoid timezone issues
        // Start date is the 1st of the month at 00:00:00.000 UTC
        const startDate = new Date(
          Date.UTC(year, actualMonthIndex, 1, 0, 0, 0, 0)
        );
        if (isNaN(startDate.getTime())) {
          addSystemMessage({
            level: "danger",
            message: `Invalid date for month index "${dateKey}" in channel "${channelKey}": Could not create valid date.`,
          });
          return [];
        }

        // Create end date (last day of month) using UTC to avoid timezone issues
        // End date is the last day of the month at 00:00:00.000 UTC
        const endDate = new Date(
          Date.UTC(year, actualMonthIndex + 1, 0, 0, 0, 0, 0)
        );

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          addSystemMessage({
            level: "danger",
            message: `Invalid date for month index "${dateKey}" in channel "${channelKey}": Could not create valid date.`,
          });
          return [];
        }

        // Create start and end dates as UTC milliseconds timestamps
        const start = startDate.getTime();
        const end = endDate.getTime();

        // Create the parquet row
        const row: DemandWalkParquetRow = {
          group_level: segments,
          group_value: segmentValues, // Use the parsed segment values
          start,
          end,
          quantity_override: parsedValue,
          override_type: measure,
        };

        rows.push(row);
      }
    }

    if (rows.length === 0) {
      addSystemMessage({
        level: "warning",
        message:
          "No valid data was processed. Please check your inputs and try again.",
      });
      return [];
    }

    // Return the actual data that arrayToParquetBase64 can handle
    // This will create a working parquet file with the right structure
    // Note: arrayToParquetBase64 will infer types, but we're providing the right data structure
    return rows.map((r) => ({
      group_level: r.group_level,
      group_value: r.group_value,
      start: r.start, // Already in milliseconds
      end: r.end, // Already in milliseconds
      quantity_override: r.quantity_override,
      override_type: r.override_type,
    }));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    addSystemMessage({
      level: "danger",
      message: `Critical error during parquet conversion: ${errorMessage}. Please contact support if this persists.`,
    });
    return [];
  }
};

// ============================================================================
// DEMAND PLAN (persistent layer) PARQUET CONVERSION
// ============================================================================

/** One row of the persisted demand_plan_layer.parquet: the engine InputLayersSchema
 *  (first six fields) + audit columns. Timestamps are epoch-MS here; the writer
 *  re-encodes them as Timestamp(us, UTC). */
export interface DemandPlanLayerRow {
  group_level: string[];
  group_value: string[];
  start: number; // epoch ms, inclusive window start
  end: number; // epoch ms, inclusive window end (last day)
  quantity_override: number;
  override_type: string;
  edited_by: string;
  edited_at: number; // epoch ms
}

/** Resolve a display period key/label to its inclusive [start, last-day] UTC window
 *  in epoch ms, for the two demand_plan_editable grains:
 *   - month: key is a month INDEX ("0".."11") relative to the current month, or a
 *     full month name ("January"); window = first .. last day of that month.
 *   - week: key/label is an ISO date or "MM/DD/YY" naming the week's start day;
 *     window = that day .. +6 days.
 *  Returns null when the key can't be parsed for the given grain. */
export const resolveDemandPlanWindow = (
  periodKey: string,
  grain: "month" | "week"
): { start: number; end: number } | null => {
  if (grain === "week") {
    // Accept ISO ("YYYY-MM-DD", parsed natively by dayjs.utc) or "MM/DD/YY".
    // customParseFormat is NOT loaded, so parse MM/DD/YY by hand rather than via
    // a format string.
    let day = dayjs.utc(periodKey);
    if (!day.isValid()) {
      const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(periodKey.trim());
      if (m) {
        const mm = parseInt(m[1], 10);
        const dd = parseInt(m[2], 10);
        const yy = parseInt(m[3], 10);
        const fullYear = m[3].length === 2 ? 2000 + yy : yy;
        day = dayjs.utc(Date.UTC(fullYear, mm - 1, dd));
      }
    }
    if (!day.isValid()) return null;
    const start = day.startOf("day");
    // end = LAST day of the week (start + 6d) — engine's inclusive convention
    // (datetime_ranges closed="both"); start+7 would scale a day into next week.
    return { start: start.valueOf(), end: start.add(6, "day").valueOf() };
  }

  // month grain
  const monthLabels = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const now = dayjs.utc();
  let year: number;
  let monthIdx0: number; // 0-based month of the resolved period

  const asIndex = parseInt(periodKey, 10);
  if (!isNaN(asIndex) && String(asIndex) === periodKey.trim()) {
    // Month index relative to the current month (0 == current month).
    if (asIndex < 0 || asIndex > 11) return null;
    const total = now.month() + asIndex;
    year = now.year() + Math.floor(total / 12);
    monthIdx0 = total % 12;
  } else {
    // Full month name; rolls to next year when the named month is earlier than the
    // current one (matches the display labelling in createDemandWalkColumns).
    const named = monthLabels.findIndex(
      (m) => m.toLowerCase() === periodKey.trim().toLowerCase()
    );
    if (named === -1) return null;
    monthIdx0 = named;
    year = named + 1 < now.month() + 1 ? now.year() + 1 : now.year();
  }

  const start = Date.UTC(year, monthIdx0, 1, 0, 0, 0, 0);
  // end = LAST day of the month (day 0 of next month) — engine's inclusive convention
  // (datetime_ranges closed="both", same as customer input_forecast_layers). Using
  // first-of-next-month leaked the override one day into the following bucket.
  const end = Date.UTC(year, monthIdx0 + 1, 0, 0, 0, 0, 0);
  return { start, end };
};

/** Migrate a window `end` from the legacy half-open (first-of-next-period) convention
 *  to the inclusive LAST-DAY one. IDEMPOTENT (recomputes last-day from `start` + grain
 *  inferred from duration), so it's a no-op on an already-correct end. Used on read-back
 *  so a saved row keys identically to a fresh one — else duplicate row + engine double-apply. */
export const normalizeWindowEndToLastDay = (
  startMs: number,
  endMs: number
): number => {
  const start = dayjs.utc(startMs);
  const durationDays = dayjs.utc(endMs).diff(start, "day");
  if (durationDays >= 20) {
    // monthly window → last day of the start month
    return start.add(1, "month").subtract(1, "day").startOf("day").valueOf();
  }
  if (durationDays >= 5) {
    // weekly window → last day of the week (start + 6 days)
    return start.add(6, "day").startOf("day").valueOf();
  }
  return endMs;
};

/** Stable key for a layer cell's identity = (group_level, group_value, start, end).
 *  Used to upsert this session's edits over the prior layer and mark reverted cells
 *  for deletion at the same grain. Keep in sync with the read-back key. */
export const demandPlanLayerKey = (
  groupLevel: string[],
  groupValue: string[],
  startMs: number,
  endMs: number
): string =>
  JSON.stringify([groupLevel.join(","), groupValue.join(","), startMs, endMs]);

/** Conversion result: the upsert rows AND the cleared-cell keys. The cleared set is
 *  required for cross-session merge — a blank cell emits no row, so without an explicit
 *  key the merge couldn't tell a prior override was reverted (the bug this fixes). */
export interface DemandPlanLayerConversion {
  rows: DemandPlanLayerRow[];
  /** Keys (demandPlanLayerKey) of cells the planner CLEARED this session. */
  clearedKeys: Set<string>;
}

/** Build this session's demand_plan layer rows from finalForecastInputs (month + week
 *  windows, stamped with edited_by / edited_at). A blank / "-" cell is a REVERT: it
 *  emits no row but records its key in `clearedKeys` so the caller deletes the matching
 *  prior override. Fully-elapsed windows (end <= todayMs) are pruned. */
export const convertDemandPlanInputsToParquet = (
  finalForecastInputs: Record<string, Record<string, string>>,
  segments: string[],
  measure: string,
  grain: "month" | "week",
  userEmail: string,
  nowMs: number,
  addSystemMessage: (message: { level: any; message: string }) => void
): DemandPlanLayerConversion => {
  const rows: DemandPlanLayerRow[] = [];
  const clearedKeys = new Set<string>();

  if (!finalForecastInputs || typeof finalForecastInputs !== "object") {
    return { rows, clearedKeys };
  }
  if (!segments || segments.length === 0) {
    addSystemMessage({
      level: "danger",
      message:
        "Cannot save demand plan edits without segmentation information (segments).",
    });
    return { rows, clearedKeys };
  }

  // Prune anything that has fully elapsed (window end at or before today).
  const todayStart = dayjs.utc(nowMs).startOf("day").valueOf();

  for (const [segKey, periodInputs] of Object.entries(finalForecastInputs)) {
    if (!periodInputs || typeof periodInputs !== "object") continue;
    const segValues = segKey.split("|");
    if (segValues.length !== segments.length) {
      addSystemMessage({
        level: "danger",
        message: `Segment mismatch for key "${segKey}": expected ${segments.length} values, found ${segValues.length}.`,
      });
      continue;
    }

    for (const [periodKey, raw] of Object.entries(periodInputs)) {
      const parsed = parseInputValue(String(raw ?? ""));

      const window = resolveDemandPlanWindow(periodKey, grain);
      if (!window) {
        addSystemMessage({
          level: "danger",
          message: `Could not resolve a ${grain} window for period "${periodKey}" (segment "${segKey}").`,
        });
        continue;
      }
      if (window.end <= todayStart) continue; // prune fully-elapsed periods

      if (parsed === null) {
        // Blank / dash / empty == revert this cell to baseline: emit NO row, but
        // record the key so the caller drops any matching prior override.
        clearedKeys.add(
          demandPlanLayerKey(segments, segValues, window.start, window.end)
        );
        continue;
      }

      rows.push({
        group_level: segments,
        group_value: segValues,
        start: window.start,
        end: window.end,
        quantity_override: parsed,
        override_type: measure,
        edited_by: userEmail,
        edited_at: nowMs,
      });
    }
  }

  return { rows, clearedKeys };
};
